import {
  CollectiveAdvice,
  NeedName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  DownloadContainer,
  SavingAdvicesMessage,
} from "../../ProductPrice/AdviceMessages/AdviceDiff/styled";

import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { ReduxState } from "Store/rootReducer";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { useIsAdvicePriceLoading } from "../../ProductPrice/hooks/stateSelectors";
import { useEntrance } from "../../ProductPrice/hooks/useEntrance";
import { ObliqueDescription } from "../../adviceStyled";
import { AdviceCards } from "../AkaAdviceCards";
import { AdviceTotalPrice } from "../AkaAdviceCards/AdvicePrice/AdviceTotalPrice";
import { AkaAdvicePriceFormValues } from "../AkaAdviceCards/interfaces";
import { AkaAdviceScreenshot } from "../AkaAdviceScreenshot/AkaAdviceScreenshot";
import { sortNeeds } from "../utils";
import { CollectiveAdviceIntro } from "./CollectiveAdviceIntro";

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  collectiveAdvice: CollectiveAdvice;
  akaAdviceScreenshot: AkaAdviceScreenshot;
  handleTakeScreenshot: (formProps: AkaAdvicePriceFormValues) => void;
}

export const CollectiveAdviceForm = ({
  formProps,
  collectiveAdvice,
  akaAdviceScreenshot,
  handleTakeScreenshot,
}: Props): React.ReactElement => {
  const entrance = useEntrance();
  const isAdvisor = useIsAdvisorSelector();
  const priceError = useSelector(
    (state: ReduxState) => state.akaAdvice.advicePriceCalculationResults.error,
  );
  const isPriceLoading = useIsAdvicePriceLoading();
  const addProductToCart = useSelector(
    (state: ReduxState) => state.addProductToCart,
  );

  const formHasErrors = !formProps.isValid;
  const weights = !isDefined(entrance) ? {} : { [entrance]: 0.1 };
  const sortedNeeds = sortNeeds(
    Object.entries(collectiveAdvice.needs).map(([name, coverageNeed]) => ({
      name: name as NeedName,
      coverageNeed,
    })),
    weights,
  );

  return (
    <div ref={akaAdviceScreenshot.screenshotRefCallback}>
      <CollectiveAdviceIntro
        collectiveAdvice={collectiveAdvice}
        sortedNeeds={sortedNeeds}
      />
      <AdviceCards
        isCollectiveAdvice={true}
        formProps={formProps}
        collectiveAdvice={collectiveAdvice}
        addedWeights={weights}
      />
      <SavingAdvicesMessage className="AdviceDiffCard">
        <DownloadContainer
          className="AdviceDownloadContainer"
          withMargin={!isAdvisor}
        >
          <ThemedButton
            type="button"
            id="advice__download-button"
            trackId={buttonTrackId("Download the entire advice")}
            iconLeft="download"
            variant="text"
            onClick={() => handleTakeScreenshot(formProps.values)}
            disabled={
              isPriceLoading ||
              isDefined(priceError) ||
              addProductToCart.isLoading ||
              formHasErrors
            }
          >
            {"Last ned hele anbefalingen (PDF)"}
          </ThemedButton>
          <ObliqueDescription fw={400} size="16px">
            {isAdvisor
              ? "Når du har lastet ned dette dokumentet kan du også finne det igjen på kunden i OnBase"
              : "Når du har lastet ned dette dokumentet kan du også finne det igjen på storebrand.no/arkiv"}
          </ObliqueDescription>
          {akaAdviceScreenshot.screenshotIsLoading && <ThemedSpinner />}
        </DownloadContainer>
      </SavingAdvicesMessage>
      <AdviceTotalPrice formProps={formProps} />
    </div>
  );
};
